import moment from 'moment';
import ko from 'knockout';
import {closePopups, openPopup} from '../../init';
import {customFetch as fetch} from '../../fetch';
import {errorToast} from "../../toastr-helper";

export interface EditShiftModelOptions {
    endpoint: string;
    saveCallback?: ((response: any) => void);
}

class EventModel {
    id: KnockoutObservable<string>;
    name: KnockoutObservable<string>;
    startDate: KnockoutObservable<moment.Moment>;
    endDate: KnockoutObservable<moment.Moment>;
    revenue: KnockoutObservable<number>;

    nameIncludingDate: KnockoutComputed<string>;

    constructor(model: any = {}) {
        this.id = ko.observable<string>(model.id || '');
        this.name = ko.observable<string>(model.name || '');
        this.startDate = ko.observable<moment.Moment>(moment(model.startDate));
        this.endDate = ko.observable<moment.Moment>(moment(model.endDate));
        this.revenue = ko.observable<number>(model.revenue);
        this.nameIncludingDate = ko.pureComputed<string>(() => `${this.name()} (${this.startDate().format('YYYY-MM-DD HH:mm')} - ${this.endDate().format('YYYY-MM-DD HH:mm')} )`);
    }
}

export class EditShiftModel {
    isInitializing: KnockoutObservable<boolean>;
    clockedUnitId: KnockoutObservable<string>;
    timeWorking: KnockoutObservable<number>;
    timeBreak: KnockoutObservable<number>;
    date: KnockoutObservable<moment.Moment>;
    timePartFrom: KnockoutObservable<string>;
    timePartTo: KnockoutObservable<string>;
    loading: KnockoutObservable<boolean>;
    formattedDate: KnockoutComputed<string>;
    selectedEventId: KnockoutObservable<string>;

    events: KnockoutObservableArray<EventModel>;
    filteredEvents: KnockoutComputed<EventModel[]>;

    options: EditShiftModelOptions;

    constructor(options: EditShiftModelOptions) {
        this.options = options;

        this.isInitializing = ko.observable<boolean>(false);
        this.clockedUnitId = ko.observable<string>();
        this.timeWorking = ko.observable<number>();
        this.timeBreak = ko.observable<number>();
        this.loading = ko.observable<boolean>();
        this.timePartFrom = ko.observable<string>();
        this.timePartTo = ko.observable<string>();
        this.date = ko.observable<moment.Moment>();
        this.selectedEventId = ko.observable<string>('');

        this.timePartFrom.subscribe((newValue) => {
            if (newValue) {
                this.calculateNewWorkingAndBreakTime(newValue, this.timePartTo(), this.timeBreak());
            }
        });
        this.timePartTo.subscribe((newValue) => {
            if (newValue) {
                this.calculateNewWorkingAndBreakTime(this.timePartFrom(), newValue, this.timeBreak());
            }
        });
        this.timeBreak.subscribe((newValue) => {
            this.calculateNewWorkingAndBreakTime(this.timePartFrom(), this.timePartTo(), newValue);
        });

        this.formattedDate = ko.pureComputed(() => this.date() ? this.date().format('DD-MM-YYYY') : '');

        this.events = ko.observableArray<EventModel>();

        this.filteredEvents = ko.pureComputed(() => {
            const date = this.date();
            return this.events().filter((e: EventModel) => date.isBetween(e.startDate(), e.endDate().endOf('day')));
        });
    }

    calculateNewWorkingAndBreakTime(timePartFrom: string, timePartTo: string, timeBreakStr: number) {
        if (!this.isInitializing()) {
            const startTime = this.getDateFromTimeParts(true, timePartFrom, timePartTo)!;
            const endTime = this.getDateFromTimeParts(false, timePartFrom, timePartTo)!;

            const timeBreak = moment.duration(timeBreakStr).asMilliseconds();

            if (startTime.isValid() && endTime.isValid()) {
                const totalTimeSpanMs = moment.duration(endTime.diff(startTime)).asMilliseconds();
                const timeWorkedMs = Math.max(totalTimeSpanMs - timeBreak, 0);

                if (timeBreak > totalTimeSpanMs) {
                    errorToast('U heeft meer pauzetijd opgegeven dan daadwerkelijk gewerkt is, waardoor de gewerkte tijd niet berekend kan worden. Controleer uw invoer op fouten.');
                    this.timeBreak(totalTimeSpanMs);
                }

                if (timeWorkedMs === 0) {
                    //Hacky fix to trigger re-render of observable. Apparantly setting KnockoutObservable<number> to 0 doesn't trigger subscriptions
                    this.timeWorking(10000);
                }

                this.timeWorking(timeWorkedMs);
            } else {
                errorToast('Kon gewerkte tijd niet berekenen, controleer uw invoer op fouten.')
            }
        }
    }

    setEvents(model: any[]) {
        this.events = ko.observableArray<EventModel>((model || [])
            .map((e: any) => new EventModel(e))
            .sort((a: EventModel, b: EventModel) => a.name().toLowerCase() > b.name().toLowerCase() ? 1 : -1));

        this.filteredEvents = ko.pureComputed(() => {
            const date = this.date();
            return this.events().filter((e: EventModel) => date.isBetween(e.startDate(), e.endDate().endOf('day')));
        });
    }

    init(model: any = {}) {

        this.isInitializing(true);

        this.events = ko.observableArray<EventModel>((model.events || [])
            .map((e: any) => new EventModel(e))
            .sort((a: EventModel, b: EventModel) => a.name().toLowerCase() > b.name().toLowerCase() ? 1 : -1));

        this.filteredEvents = ko.pureComputed(() => {
            const date = this.date();
            return this.events().filter((e: EventModel) => date.isBetween(e.startDate(), e.endDate().endOf('day')));
        });

        this.clockedUnitId(model.id);
        this.timeWorking(model.timeWorked);
        this.timeBreak(model.timeBreak);
        this.date(moment(model.startDate));
        this.timePartFrom(moment(model.startDate).format('HH:mm'));
        this.timePartTo(moment(model.endDate).format('HH:mm'));

        this.selectedEventId(model.selectedEventId || "");

        this.isInitializing(false);

        openPopup('edit-job');
    }

    private getDate(isStartDate: boolean): moment.Moment | null {
        return this.getDateFromTimeParts(isStartDate, this.timePartFrom(), this.timePartTo());
    }

    private getDateFromTimeParts(isStartDate: boolean, timePartFrom: string, timePartTo: string): moment.Moment | null {
        if (!this.date()) {
            return null;
        }

        const timeParts = isStartDate
            ? [timePartFrom, timePartTo]
            : [timePartTo, timePartFrom];

        const dateFormat = `${this.date().format('MM-DD-YYYY')}`;
        let date1 = moment(`${dateFormat} ${timeParts[0]}`, 'MM-DD-YYYY HH:mm');
        const date2 = moment(`${dateFormat} ${timeParts[1]}`, 'MM-DD-YYYY HH:mm');

        //If the enddate's time part is before the startdate's time part, we can assume the end date is the next day
        if (!isStartDate && date1.isBefore(date2, 'hour')) {
            let dateToAdd = moment(`${dateFormat}`, 'MM-DD-YYYY').add(1, 'day');
            const updatedDateFormat = `${dateToAdd.format('MM-DD-YYYY')}`;
            date1 = moment(`${updatedDateFormat} ${timeParts[0]}`, 'MM-DD-YYYY HH:mm');
        }

        if (!date1.isValid()) {
            return null;
        }

        return date1;
    }

    async submit() {
        const fd = new FormData();
        fd.append('id', this.clockedUnitId());
        fd.append('startDate', this.getDate(true)!.format('YYYY-MM-DD HH:mm'));
        fd.append('endDate', this.getDate(false)!.format('YYYY-MM-DD HH:mm'));
        fd.append('timeWorked', moment.duration(this.timeWorking()).asMilliseconds().toString());
        fd.append('timeBreak', moment.duration(this.timeBreak()).asMilliseconds().toString());
        fd.append('eventId', this.selectedEventId() || '');

        this.loading(true);

        const response = await fetch(this.options.endpoint, {
            method: 'POST',
            body: fd
        });

        this.loading(false);

        if (response.ok) {
            closePopups();

            if (this.options.saveCallback) {
                this.options.saveCallback(response.data);
            }
        }
    }
}
